import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { supabase } from "@/integrations/supabase/client";

const IOS_CLIENT_ID = "553505008806-lepnafos8jpgc9oe7di76hqpefc7702d.apps.googleusercontent.com";

let initialized = false;

function describeError(error: unknown) {
  if (error instanceof Error) return { name: error.name, message: error.message, code: (error as { code?: string }).code };
  if (error && typeof error === "object") return error;
  return { message: String(error) };
}

function idTokenAudience(token: string): string | string[] | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(normalized)) as { aud?: string | string[] };
    return claims.aud ?? null;
  } catch {
    return null;
  }
}

async function initializeGoogle(): Promise<void> {
  if (initialized) return;
  console.info("[NativeGoogleAuth] initializing SocialLogin Google provider");
  await SocialLogin.initialize({
    google: {
      iOSClientId: IOS_CLIENT_ID,
      // Do not set iOSServerClientId in online mode. Doing so changes the
      // id_token audience to the web client, while native auth accepts the
      // registered iOS client audience.
      mode: "online",
    },
  });
  initialized = true;
}

export function isNativeGoogleAuthAvailable(): boolean {
  try {
    const isNativeIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
    if (isNativeIos && !Capacitor.isPluginAvailable("SocialLogin")) {
      console.warn("[NativeGoogleAuth] SocialLogin plugin is not installed in this iOS build; rebuild/sync the native app");
    }
    return isNativeIos;
  } catch {
    return false;
  }
}

export async function signInWithNativeGoogle(): Promise<{ error: Error | null }> {
  try {
    console.info("[NativeGoogleAuth] Starting native Google sign-in via SocialLogin");
    if (!Capacitor.isPluginAvailable("SocialLogin")) {
      return { error: new Error("The iOS app needs to be rebuilt with the native Google sign-in plugin") };
    }

    await initializeGoogle();

    const login = await SocialLogin.login({
      provider: "google",
      options: { scopes: ["email", "profile"], forcePrompt: true },
    });

    if (login.result.responseType !== "online" || !login.result.idToken) {
      return { error: new Error("Google did not return an identity token") };
    }

    const audience = idTokenAudience(login.result.idToken);
    console.info("[NativeGoogleAuth] received Google identity token", { audience });

    const { error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: login.result.idToken,
      access_token: login.result.accessToken?.token,
    });

    if (error) return { error };
    console.info("[NativeGoogleAuth] native Google sign-in completed");
    return { error: null };
  } catch (e) {
    console.error("[NativeGoogleAuth] Native Google sign-in failed", describeError(e));
    const error = e instanceof Error ? e : new Error("Native Google sign-in failed");
    return { error };
  }
}
